import '@/App.css';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { CurrencyProvider } from '@/context/CurrencyContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { ConfirmProvider } from '@/context/ConfirmContext';
import { SiteSettingsProvider } from '@/context/SiteSettingsContext';
import { AdminViewProvider } from '@/context/AdminViewContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProtectedRoute from '@/components/ProtectedRoute';
import FloatingAdminReturn from '@/components/FloatingAdminReturn';
import { track } from '@/lib/analytics';

import Landing from '@/pages/Landing';
import Shop from '@/pages/Shop';
import ProductDetail from '@/pages/ProductDetail';
import DesignEditor from '@/pages/DesignEditor';
import DesignsLibrary from '@/pages/DesignsLibrary';
import TopRated from '@/pages/TopRated';
import Community, { CommunityDetail } from '@/pages/Community';
import AIGenerator from '@/pages/AIGenerator';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import Orders from '@/pages/Orders';
import OrderDetail from '@/pages/OrderDetail';
import Profile from '@/pages/Profile';
import MyDesigns from '@/pages/MyDesigns';
import Favorites from '@/pages/Favorites';
import Notifications from '@/pages/Notifications';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import AdminDashboard from '@/pages/AdminDashboard';
import LegalPage from '@/pages/LegalPage';
import ContactPage from '@/pages/ContactPage';
import AuthCallback from '@/pages/AuthCallback';
import NotFound from '@/pages/NotFound';
import BookmarkFlyer from '@/components/BookmarkFlyer';
import MobileBottomNav from '@/components/MobileBottomNav';
import DesignDetail from '@/pages/DesignDetail';

const Shell = ({ children, noFooter, noMobileNav }) => (
  <div className="App min-h-screen flex flex-col">
    <Header />
    <main className="flex-1 pb-16 md:pb-0">{children}</main>
    {!noFooter && <Footer />}
    {!noMobileNav && <MobileBottomNav />}
  </div>
);

// Auto-tracks a page_view on every route change (excludes /admin/*)
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    track('page_view', { path: location.pathname });
  }, [location.pathname]);
  return null;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <AdminViewProvider>
            <SiteSettingsProvider>
            <CartProvider>
              <ConfirmProvider>
              <BrowserRouter>
              <RouteTracker />
              <Routes>
                <Route path="/" element={<Shell><Landing /></Shell>} />
                <Route path="/shop" element={<Shell><Shop /></Shell>} />
                <Route path="/shop/:type" element={<Shell><Shop /></Shell>} />
                <Route path="/product/:id" element={<Shell><ProductDetail /></Shell>} />
                <Route path="/design/:productId" element={<Shell noFooter noMobileNav><DesignEditor /></Shell>} />
                <Route path="/designs" element={<Shell><DesignsLibrary /></Shell>} />
                <Route path="/designs/:id" element={<Shell><DesignDetail /></Shell>} />
                <Route path="/top-rated" element={<Shell><TopRated /></Shell>} />
                <Route path="/community" element={<Shell><Community /></Shell>} />
                <Route path="/community/:id" element={<Shell><CommunityDetail /></Shell>} />
                <Route path="/ai-generator" element={<Shell><AIGenerator /></Shell>} />
                <Route path="/cart" element={<Shell><ProtectedRoute><Cart /></ProtectedRoute></Shell>} />
                <Route path="/checkout" element={<Shell><ProtectedRoute><Checkout /></ProtectedRoute></Shell>} />
                <Route path="/orders" element={<Shell><ProtectedRoute><Orders /></ProtectedRoute></Shell>} />
                <Route path="/orders/:id" element={<Shell><ProtectedRoute><OrderDetail /></ProtectedRoute></Shell>} />
                <Route path="/profile" element={<Shell><ProtectedRoute><Profile /></ProtectedRoute></Shell>} />
                <Route path="/my-designs" element={<Shell><ProtectedRoute><MyDesigns /></ProtectedRoute></Shell>} />
                <Route path="/favorites" element={<Shell><ProtectedRoute><Favorites /></ProtectedRoute></Shell>} />
                <Route path="/notifications" element={<Shell><ProtectedRoute><Notifications /></ProtectedRoute></Shell>} />
                <Route path="/login" element={<Shell><Login /></Shell>} />
                <Route path="/register" element={<Shell><Register /></Shell>} />
                <Route path="/admin/*" element={<Shell><ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute></Shell>} />
                <Route path="/legal/:slug" element={<Shell><LegalPage /></Shell>} />
                <Route path="/contact" element={<Shell><ContactPage /></Shell>} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                {/* Global catch-all — landing page for any bad URL, custom domain typos, or expired links */}
                <Route path="*" element={<Shell><NotFound /></Shell>} />
              </Routes>
              <Toaster richColors={false} position="top-right" />
              <BookmarkFlyer />
              <FloatingAdminReturn />
            </BrowserRouter>
            </ConfirmProvider>
            </CartProvider>
            </SiteSettingsProvider>
            </AdminViewProvider>
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
